import { LogOut, User, Users, LayoutDashboard, Settings as SettingsIcon, Building2 } from 'lucide-react'
import { useTaskStore } from '@/store/useTaskStore'
import { useAuthStore } from '@/store/useAuthStore'
import { Button } from '@/components/ui/button'

interface HeaderProps {
  currentView: 'dashboard' | 'users' | 'clients' | 'settings'
  setCurrentView: (view: 'dashboard' | 'users' | 'clients' | 'settings') => void
}

export function Header({ currentView, setCurrentView }: HeaderProps) {
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId)
  const tasks = useTaskStore((s) => s.tasks)
  const selectedTask = tasks.find((t) => t.id === selectedTaskId)
  const { user, logout } = useAuthStore()

  const handleLogout = async () => {
    try {
      await logout()
      window.location.reload()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <header className="flex flex-col gap-3 border-b border-slate-800 pb-3 xl:flex-row xl:items-center xl:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-xl font-semibold">ATLAS</h1>
          <span className="text-sm text-slate-500">Growth Pipeline Manager</span>
        </div>
      </div>

      <div className="flex flex-col gap-2 xl:items-end">
        {selectedTask && currentView === 'dashboard' && (
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <span className="text-sm text-slate-400">Current Task:</span>
            <span className="truncate font-medium">{selectedTask.name}</span>
          </div>
        )}

        {user && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-1">
              <Button
                variant={currentView === 'dashboard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCurrentView('dashboard')}
                className="gap-2"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Button>
              <Button
                variant={currentView === 'clients' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCurrentView('clients')}
                className="gap-2"
              >
                <Building2 className="w-4 h-4" />
                Clients
              </Button>
              {user.role === 'admin' && (
                <>
                  <Button
                    variant={currentView === 'users' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentView('users')}
                    className="gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Users
                  </Button>
                  <Button
                    variant={currentView === 'settings' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setCurrentView('settings')}
                    className="gap-2"
                  >
                    <SettingsIcon className="w-4 h-4" />
                    Settings
                  </Button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 border border-slate-800 px-2.5 py-1.5">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium">{user.name}</span>
              {user.role === 'admin' && (
                <span className="text-xs px-1.5 py-0.5 bg-slate-800 text-slate-300">
                  Admin
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        )}
      </div>
    </header>
  )
}
